/* iter187 — Campaign/Promo Entries Drawer.
   Right-side sheet that lists every entry for a specific campaign or promo_link
   with search, status filter, consent filter, duplicate filter, per-row status
   editor, and CSV export. Same UI works for both scopes; the scope prop tells
   it which backend endpoint to hit ("campaign" vs "promo").
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Download, RefreshCw, ChevronDown, MailCheck, PhoneCall,
  Copy, ShieldCheck, AlertOctagon,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'booked', label: 'Booked' },
  { value: 'closed', label: 'Closed' },
  { value: 'duplicate', label: 'Duplicate / Already Entered' },
  { value: 'disqualified', label: 'Disqualified' },
];

const STATUS_BADGE = {
  new:          'text-blue-200 bg-blue-500/15 border-blue-500/40',
  contacted:    'text-purple-200 bg-purple-500/15 border-purple-500/40',
  booked:       'text-green-300 bg-green-500/15 border-green-500/40',
  closed:       'text-[#A3A3A3] bg-[#1a1a1a] border-[#3a3a3a]',
  duplicate:    'text-amber-200 bg-amber-500/15 border-amber-500/40',
  disqualified: 'text-red-300 bg-red-500/15 border-red-500/40',
};

const fmtDT = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const smsBadge = (s) => {
  if (s === 'sent')                 return 'text-green-300 bg-green-500/15 border-green-500/40';
  if (s === 'skipped_opted_out')    return 'text-[#A3A3A3] bg-[#1a1a1a] border-[#3a3a3a]';
  if (s === 'not_attempted')        return 'text-[#A3A3A3] bg-[#1a1a1a] border-[#3a3a3a]';
  if (s === 'error')                return 'text-red-300 bg-red-500/15 border-red-500/40';
  return 'text-amber-200 bg-amber-500/15 border-amber-500/40';
};

const PromoEntriesDrawer = ({ open, onOpenChange, scope, id, name }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [consentFilter, setConsentFilter] = useState('all');
  const [duplicateFilter, setDuplicateFilter] = useState('all');

  const endpoint = scope === 'promo' ? 'promos' : 'campaigns';

  const load = useCallback(async () => {
    if (!open || !id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (consentFilter !== 'all') params.set('consent', consentFilter);
      if (duplicateFilter !== 'all') params.set('duplicate', duplicateFilter);
      const { data } = await axios.get(`${API}/admin/${endpoint}/${id}/entries?${params}`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load entries');
    }
    setLoading(false);
  }, [open, id, endpoint, q, statusFilter, consentFilter, duplicateFilter]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/admin/promo-leads/${leadId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (consentFilter !== 'all') params.set('consent', consentFilter);
      if (duplicateFilter !== 'all') params.set('duplicate', duplicateFilter);
      const res = await axios.get(
        `${API}/admin/${endpoint}/${id}/entries.csv?${params}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'entries').replace(/\s+/g, '_')}_entries.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  const dupCount = data?.duplicate_attempt_total || 0;
  const items = data?.items || [];

  const copy = (v) => { if (v) { navigator.clipboard.writeText(v).catch(() => {}); toast.success('Copied'); } };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-4xl overflow-y-auto"
        data-testid="promo-entries-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D4AF37]" />
            Entries — <span className="text-[#D4AF37]">{name || (data?.campaign?.name || data?.promo?.name || '')}</span>
          </SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">
            {data
              ? <><span className="text-white font-semibold" data-testid="entries-count">{data.count}</span> entries · {dupCount} duplicate {dupCount === 1 ? 'attempt' : 'attempts'}</>
              : (loading ? 'Loading…' : 'Waiting…')}
          </SheetDescription>
        </SheetHeader>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
            <Input
              data-testid="entries-search"
              placeholder="Search name, phone, email, zip…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="entries-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={consentFilter} onValueChange={setConsentFilter}>
            <SelectTrigger data-testid="entries-consent-filter"><SelectValue placeholder="Consent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All consent</SelectItem>
              <SelectItem value="yes">Consent = yes</SelectItem>
              <SelectItem value="no">Consent = no</SelectItem>
            </SelectContent>
          </Select>
          <Select value={duplicateFilter} onValueChange={setDuplicateFilter}>
            <SelectTrigger data-testid="entries-duplicate-filter"><SelectValue placeholder="Duplicates" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entries</SelectItem>
              <SelectItem value="with_duplicates">Has duplicate attempts</SelectItem>
              <SelectItem value="no_duplicates">No duplicate attempts</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2 sm:col-span-3">
            <Button data-testid="entries-refresh" size="sm" onClick={load} disabled={loading} variant="outline">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button data-testid="entries-export-csv" size="sm" onClick={exportCsv} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="mt-4 space-y-2" data-testid="entries-list">
          {loading && !data ? (
            <div className="text-center text-[#A3A3A3] py-10">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 border border-[#262626] rounded bg-[#0d0d0d] text-[#A3A3A3]" data-testid="entries-empty">
              No entries yet.
            </div>
          ) : items.map((it) => (
            <EntryCard key={it.id} entry={it} onStatus={(s) => updateStatus(it.id, s)} onCopy={copy} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const EntryCard = ({ entry, onStatus, onCopy }) => {
  const [expanded, setExpanded] = useState(false);
  const dupCount = entry.duplicate_attempt_count || 0;
  return (
    <div className="border border-[#262626] rounded-md bg-[#0d0d0d] p-3" data-testid={`entry-row-${entry.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm">
              {(entry.first_name || '') + ' ' + (entry.last_name || '')}
            </span>
            <Badge className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${STATUS_BADGE[entry.status] || STATUS_BADGE.new}`}>
              {entry.status || 'new'}
            </Badge>
            {entry.matched_existing_customer && (
              <Badge className="text-[10px] bg-[#1a1a1a] border-[#3a3a3a] text-[#A3A3A3]">Existing customer</Badge>
            )}
            {dupCount > 0 && (
              <Badge className="text-[10px] font-bold text-amber-300 bg-amber-500/15 border-amber-500/40 inline-flex items-center gap-1" title={`Last attempt: ${fmtDT(entry.last_duplicate_attempt_at)}`}>
                <AlertOctagon className="w-3 h-3" /> {dupCount} duplicate {dupCount === 1 ? 'attempt' : 'attempts'}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-[#A3A3A3] flex-wrap">
            <span className="inline-flex items-center gap-1"><PhoneCall className="w-3 h-3" />
              <button onClick={() => onCopy(entry.phone)} className="hover:text-white">{entry.phone || '—'}</button>
            </span>
            {entry.email && (
              <span className="inline-flex items-center gap-1"><MailCheck className="w-3 h-3" />
                <button onClick={() => onCopy(entry.email)} className="hover:text-white">{entry.email}</button>
              </span>
            )}
            <span>Zip {entry.zip || '—'}</span>
            {entry.source_url && <span className="text-[#737373]">from <span className="text-[#D4AF37] font-mono">{entry.source_url}</span></span>}
            <span className="text-[#525252]">{fmtDT(entry.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={entry.status || 'new'} onValueChange={onStatus}>
            <SelectTrigger data-testid={`entry-status-${entry.id}`} className="w-[180px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)} data-testid={`entry-expand-${entry.id}`}>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#262626] grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
          <Detail label="Address">{entry.address || '—'}</Detail>
          <Detail label="City">{entry.city || '—'}</Detail>
          <Detail label="Need / Interest">{(entry.needs || []).join(', ') || '—'}</Detail>
          <Detail label="Urgency">{entry.urgency || '—'}</Detail>
          <Detail label="Preferred Contact">{entry.preferred_contact || '—'}</Detail>
          <Detail label="Notes"><span className="whitespace-pre-wrap">{entry.notes || '—'}</span></Detail>
          <Detail label="Campaign">{entry.campaign_name || '—'} <span className="text-[#525252]">({entry.campaign_type})</span></Detail>
          <Detail label="Promo Code / Entry Code">{entry.promo_code || '—'}</Detail>
          <Detail label="Slug">{entry.slug || '—'}</Detail>
          <Detail label="Source URL">{entry.source_url || '—'}</Detail>
          <Detail label="Referrer" mono>{entry.referrer || '—'}</Detail>
          <Detail label="IP" mono>{entry.ip || '—'}</Detail>
          <Detail label="User Agent" mono full>{entry.user_agent || '—'}</Detail>
          <Detail label="Consent">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className={`w-3 h-3 ${entry.consent ? 'text-green-300' : 'text-red-300'}`} />
              {entry.consent ? 'Yes' : 'No'}
              {entry.consent_at && <span className="text-[#525252] ml-1">· {fmtDT(entry.consent_at)}</span>}
            </span>
          </Detail>
          <Detail label="Rules Accepted">
            {entry.rules_consent == null
              ? <span className="text-[#525252]">n/a (offer campaign)</span>
              : (entry.rules_consent
                ? <span className="text-green-300">Yes {entry.rules_consent_at && <span className="text-[#525252]">· {fmtDT(entry.rules_consent_at)}</span>}</span>
                : <span className="text-red-300">No</span>)}
          </Detail>
          <Detail label="Customer SMS">
            <Badge className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${smsBadge(entry.customer_sms_status)}`}>
              {entry.customer_sms_status}
            </Badge>
          </Detail>
          <Detail label="Office SMS">
            <Badge className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${smsBadge(entry.office_sms_status)}`}>
              {entry.office_sms_status}
            </Badge>
          </Detail>

          {dupCount > 0 && (
            <div className="sm:col-span-2 mt-2 pt-2 border-t border-[#262626]">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">Duplicate attempts</div>
              <ul className="space-y-1 text-[11px] text-[#A3A3A3]">
                {(entry.duplicate_attempts || []).map((a, i) => (
                  <li key={i} className="border-l-2 border-amber-500/40 pl-2">
                    <span className="text-white">{fmtDT(a.attempted_at)}</span>
                    {a.ip && <> · <span className="font-mono">{a.ip}</span></>}
                    {a.first_name && <> · {a.first_name} {a.last_name}</>}
                    {a.email && <> · {a.email}</>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, children, mono, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <div className="text-[9px] uppercase tracking-wider text-[#525252]">{label}</div>
    <div className={`text-white ${mono ? 'font-mono text-[11px] break-all' : 'text-[12px]'}`}>{children}</div>
  </div>
);

export default PromoEntriesDrawer;
